/* eslint-disable no-empty */
import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";

/* utils */
// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const RedirectButton = ({
  email,
  redirect_url,
  qa_mode,
  proxyUrl,
  buttonText = "Continue",
  className,
  disabled = false,
  open_in_new_tab = false,
  endpointPath = "/get-assigned-taxpro-uuid-by-email",
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const qa = useMemo(() => {
    return typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode");
  }, [qa_mode, params]);

  const PROXY_URL = useMemo(() => {
    return (
      proxyUrl ||
      window.__UK_PROXY_URL__ ||
      `${detectToolsOrigin()}/.netlify/functions/api-proxy`
    );
  }, [proxyUrl]);

  const normalizedEmail = useMemo(() => {
    return String(email || "").trim();
  }, [email]);

  const canClick = useMemo(() => {
    return !!normalizedEmail && !!redirect_url && !loading && !disabled;
  }, [normalizedEmail, redirect_url, loading, disabled]);

  const handleClick = useCallback(async () => {
    if (!canClick) return;

    try {
      setLoading(true);
      setError("");

      const { data } = await axios.post(
        PROXY_URL,
        {
          path: endpointPath,
          qa_mode: qa,
          method: "POST",
          data: {
            email: normalizedEmail,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const proId = data?.data?.taxProUuid;
      if (!proId) {
        throw new Error("pro_id was not returned from server");
      }

      const url = new URL(redirect_url, window.location.href);

      // keep all current query params unless redirect_url already has them
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        if (!url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      });

      // always set/override required params
      url.searchParams.set("pro_id", proId);

      if (qa) {
        url.searchParams.set("qa_mode", "true");
      }

      if (open_in_new_tab) {
        window.open(url.toString(), "_blank", "noopener,noreferrer");
      } else {
        window.location.assign(url.toString());
      }
    } catch (err) {
      console.error("RedirectButton error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [PROXY_URL, endpointPath, qa, normalizedEmail, redirect_url, canClick]);

  return (
    <div className="text-center">
      <button
        type="button"
        onClick={handleClick}
        disabled={!canClick}
        className={className || 'btn btn-primary'}
    
      >
        {loading ? "Loading..." : buttonText}
      </button>

      {!!error && (
        <div
          style={{
            marginTop: "8px",
            color: "#dc3545",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default RedirectButton;